import React, { useCallback, useRef } from "react";
import {
  DisplayableStack,
  DoFetchFuncProps,
  FeedProviderProps,
  SortConfig,
} from "./types";
import { FeedContext } from "./context";
import { useSearchTerm } from "src/hooks/useSearchTerm";

export const FeedProvider: React.FC<FeedProviderProps> = ({
  children,
  initialState,
  onFetchNextPage,
}) => {
  const [rows, setRows] = React.useState<any[]>(initialState?.rows || []);

  // TODO convert to useRef - not using as reactive dependency
  const { searchTerm, searchInput, setSearchInput } = useSearchTerm();
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  // TODO convert to useRef? - not using as reactive dependency
  const [page, setPage] = React.useState<number>(initialState?.page || 0);
  const pageRef = useRef(page);
  pageRef.current = page;

  const [sortConfig, setSortConfig] = React.useState<SortConfig>(
    initialState.sortConfig
  );

  // TODO convert to useRef - not using as reactive dependency
  const [scrollPosition, setScrollPosition] = React.useState<number>(
    initialState?.scrollPosition || 0
  );

  // TODO convert to useRef - not using as reactive dependency
  const [isEndOfList, setIsEndOfList] = React.useState<boolean>(
    initialState?.isEndOfList || false
  );
  const isEndOfListRef = useRef(isEndOfList);
  isEndOfListRef.current = isEndOfList;

  const [loading, setLoading] = React.useState<boolean>(
    initialState?.loading || false
  );
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const doFetch = useCallback(
    async ({
      incremental,
      pageToFetch,
      sortConfigToFetch,
    }: DoFetchFuncProps) => {
      setLoading(true);
      try {
        if (onFetchNextPage) {
          const { newData, isEOL } = await onFetchNextPage({
            page: pageToFetch ?? page,
            sortConfig: sortConfigToFetch ?? sortConfig,
            searchTerm: searchTermRef.current,
            incremental: false,
          });
          if (incremental) {
            setRows((prevRows: any[]) => [...prevRows, ...newData]);
          } else {
            setRows(newData);
          }
          // Check if we have reached the end of the list
          setIsEndOfList(isEOL);
        }
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setIsEndOfList(false);
      } finally {
        setLoading(false);
      }
    },
    [setRows, setIsEndOfList, setLoading, page, sortConfig, searchTerm]
  );

  const handleContainerScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 1;
    if (nearBottom && !loadingRef.current && !isEndOfListRef.current) {
      const currentScrollPosition = tableContainerRef.current?.scrollTop ?? 0;
      setScrollPosition(currentScrollPosition);

      // If it's not the end of the list, increment the page
      const nextPage = pageRef.current + 1;
      setPage(nextPage);
      await doFetch({ incremental: true, pageToFetch: nextPage });
    }
  };

  const handleWindowScroll = useCallback(async () => {
    const scrollTop = window.scrollY;
    const clientHeight = window.innerHeight;
    const scrollHeight = document.documentElement.scrollHeight;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 1;
    const currentScrollPosition = scrollTop ?? 0;
    setScrollPosition(currentScrollPosition);
    if (nearBottom && !loadingRef.current && !isEndOfListRef.current) {
      // If it's not the end of the list, increment the page
      const nextPage = pageRef.current + 1;
      setPage(nextPage);
      await doFetch({ incremental: true, pageToFetch: nextPage });
    }
  }, [setScrollPosition, setPage, doFetch]);

  const doReload = async () => {
    if (
      initialState &&
      (sortConfig?.direction !== initialState.sortConfig.direction ||
        sortConfig?.key !== initialState.sortConfig.key)
    ) {
      setSortConfig(initialState.sortConfig);
    }
    if (page !== 0) {
      setPage(0);
    }
    if (scrollPosition !== 0) {
      setScrollPosition(0);
    }
    await doFetch({ incremental: false, pageToFetch: 0 });
  };

  React.useEffect(() => {
    doReload();
  }, []);

  React.useEffect(() => {
    setPage(0);
    doFetch({ incremental: false, pageToFetch: 0 });
  }, [searchTerm]);

  React.useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = scrollPosition;
    } else {
      window.scrollTo(0, scrollPosition);
    }
  }, [rows]);

  const displayableStack: DisplayableStack = React.useMemo(() => {
    const stack: DisplayableStack = rows.map((row) => ({
      type: "content",
      value: row,
    }));
    if (loading) {
      stack.push({ type: "loading" });
    }
    return stack;
  }, [rows, loading]);

  const value = {
    // state
    rows,
    page,
    sortConfig,
    scrollPosition,
    isEndOfList,
    loading,
    searchTerm,
    searchInput,
    // action
    setRows,
    setPage,
    setSortConfig,
    setScrollPosition,
    setIsEndOfList,
    setLoading,
    setSearchInput,
    // utility
    scrollableProps: {
      onContainerScroll: handleContainerScroll,
      onWindowScroll: handleWindowScroll,
      containerRef: tableContainerRef,
    },
    doReload,
    doFetch,
    displayableStack,
  };

  return <FeedContext.Provider value={value}>{children}</FeedContext.Provider>;
};
